import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';

// PDF Template interfaces
export interface PdfTemplate {
  template_id: string;
  template_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface PdfTemplateContent {
  template_html: string;
  template_name: string;
  json: string;
}

export interface CreateTemplateRequest {
  template_name: string;
  template_html: string;
  json?: string;
}

export interface UpdateTemplateRequest {
  template_name?: string;
  template_html?: string;
  json?: string;
}

export interface GeneratePdfRequest {
  template_id: string;
  content: Record<string, unknown>;
  landscape?: boolean;
  single_page?: boolean;
  margin_inch?: number;
  sign_pdf?: boolean;
  filename?: string;
}

// Request for generating PDF with inline HTML template (no need to create template first)
export interface GeneratePdfWithHtmlRequest {
  template_html: string;
  content: Record<string, unknown>;
  landscape?: boolean;
  single_page?: boolean;
  margin_inch?: number;
  sign_pdf?: boolean;
  filename?: string;
}

// PDF Template Settings interfaces
// Note: template_id uses string to preserve Snowflake ID precision (numbers > 2^53 lose precision in JS)
export interface PdfTemplateSettings {
  id: number;
  entity_type: string;
  subsidiary_id?: string | number;
  center_id?: number;
  line_of_business_id?: string | number;
  form_template_id?: string | number;  // Link to specific form template
  template_id: string | number;  // Backend may return as number, but we handle as string
  is_default: boolean;
  paper_size: string;
  orientation: string;
  margin_inch: number;
  enable_signing: boolean;
  cert_config_key?: string;
  include_watermark: boolean;
  watermark_text: string;
  header_template_id?: string | number;
  footer_template_id?: string | number;
  created_at?: string;
  updated_at?: string;
}

export interface CreatePdfSettingsRequest {
  entity_type: string;
  subsidiary_id?: string | number;
  center_id?: number;
  line_of_business_id?: string | number;
  form_template_id?: string | number;  // Link to specific form template
  template_id: string | number;  // Accept both for flexibility
  is_default?: boolean;
  paper_size?: string;
  orientation?: string;
  margin_inch?: number;
  enable_signing?: boolean;
  cert_config_key?: string;
  include_watermark?: boolean;
  watermark_text?: string;
  header_template_id?: string | number;
  footer_template_id?: string | number;
}

export interface UpdatePdfSettingsRequest {
  subsidiary_id?: string | number;
  center_id?: number;
  line_of_business_id?: string | number;
  form_template_id?: string | number;  // Link to specific form template
  template_id?: string | number;
  is_default?: boolean;
  paper_size?: string;
  orientation?: string;
  margin_inch?: number;
  enable_signing?: boolean;
  cert_config_key?: string;
  include_watermark?: boolean;
  watermark_text?: string;
  header_template_id?: string | number;
  footer_template_id?: string | number;
}

export interface PdfSettingsOptions {
  entity_types: string[];
  paper_sizes: string[];
  orientations: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PdfApiService {
  private http = inject(HttpClient);
  private pdfServiceUrl = environment.pdfUrl;
  private accountingUrl = environment.accountingUrl;

  // ============================================
  // PDF Service Endpoints (Template Management)
  // ============================================

  /**
   * Get list of all PDF templates
   */
  listTemplates(): Observable<{ data: PdfTemplate[], total_records: number }> {
    return this.http.get<{ data: PdfTemplate[], total_records: number }>(
      `${this.pdfServiceUrl}/list-templates`,
      { withCredentials: true }
    );
  }

  /**
   * Get a specific template by ID
   */
  getTemplate(templateId: string): Observable<{ template_html: string; template_name: string; json: string }> {
    const params = new HttpParams().set('template_id', templateId);
    return this.http.get<{ template_html: string; template_name: string; json: string }>(
      `${this.pdfServiceUrl}/get-template`,
      { params, withCredentials: true }
    );
  }

  /**
   * Create a new PDF template
   */
  createTemplate(request: CreateTemplateRequest): Observable<{ template_id: string }> {
    return this.http.post<{ template_id: string }>(
      `${this.pdfServiceUrl}/create-template`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * Update an existing PDF template
   */
  updateTemplate(templateId: string, request: UpdateTemplateRequest): Observable<void> {
    return this.http.put<void>(
      `${this.pdfServiceUrl}/update-template/${templateId}`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * Delete a PDF template
   */
  deleteTemplate(templateId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.pdfServiceUrl}/delete-template/${templateId}`,
      { withCredentials: true }
    );
  }

  /**
   * Generate a PDF from a template
   * Returns the PDF as a blob for download
   */
  generatePdf(request: GeneratePdfRequest): Observable<Blob> {
    return this.http.post(
      `${this.pdfServiceUrl}/generate-pdf-stream`,
      request,
      { responseType: 'blob', withCredentials: true }
    );
  }

  /**
   * Generate PDF and trigger download
   */
  generateAndDownloadPdf(request: GeneratePdfRequest): void {
    this.generatePdf(request).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = request.filename || 'generated.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Failed to generate PDF:', error);
      }
    });
  }

  /**
   * Generate PDF and open in a new browser tab with print preview
   * The PDF opens in a new tab where user can view and print
   */
  generateAndOpenInNewTab(request: GeneratePdfRequest): void {
    this.generatePdf(request).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        // Open PDF in new tab
        const newTab = window.open(url, '_blank');
        if (newTab) {
          // Clean up the object URL when the tab is closed
          newTab.onload = () => {
            // Revoke URL after a delay to allow PDF to load
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          };
        } else {
          // If popup was blocked, fall back to download
          console.warn('Popup blocked, falling back to download');
          const link = document.createElement('a');
          link.href = url;
          link.download = request.filename || 'generated.pdf';
          link.click();
          window.URL.revokeObjectURL(url);
        }
      },
      error: (error) => {
        console.error('Failed to generate PDF:', error);
      }
    });
  }

  // ============================================
  // PDF Settings Endpoints (via Accounting Service)
  // ============================================

  /**
   * Get PDF settings options (entity types, paper sizes, orientations)
   */
  getPdfSettingsOptions(): Observable<{ data: PdfSettingsOptions }> {
    return this.http.get<{ data: PdfSettingsOptions }>(
      `${this.accountingUrl}/api/v1/pdf-settings/options`
    );
  }

  /**
   * Get all PDF template settings, optionally filtered by entity type
   */
  getPdfTemplateSettings(entityType?: string): Observable<{ data: PdfTemplateSettings[] }> {
    let params = new HttpParams();
    if (entityType) {
      params = params.set('entity_type', entityType);
    }
    return this.http.get<{ data: PdfTemplateSettings[] }>(
      `${this.accountingUrl}/api/v1/pdf-settings`,
      { params }
    );
  }

  /**
   * Get a specific PDF template setting by ID
   */
  getPdfTemplateSettingById(id: number): Observable<{ data: PdfTemplateSettings }> {
    return this.http.get<{ data: PdfTemplateSettings }>(
      `${this.accountingUrl}/api/v1/pdf-settings/${id}`
    );
  }

  /**
   * Get the default PDF template setting for an entity type
   * @param entityType - The entity type (e.g., 'client_invoice', 'vendor_po')
   * @param params - Optional query parameters (subsidiary_id, center_id, lob_id, form_template_id)
   */
  getDefaultPdfTemplateSetting(
    entityType: string,
    params?: {
      subsidiary_id?: number | string;
      center_id?: number | string;
      lob_id?: number | string;
      form_template_id?: number | string;
    }
  ): Observable<{ data: PdfTemplateSettings }> {
    let httpParams = new HttpParams();


    if (params) {
      if (params.subsidiary_id) httpParams = httpParams.set('subsidiary_id', params.subsidiary_id.toString());
      if (params.center_id) httpParams = httpParams.set('center_id', params.center_id.toString());
      if (params.lob_id) httpParams = httpParams.set('lob_id', params.lob_id.toString());
      if (params.form_template_id) httpParams = httpParams.set('form_template_id', params.form_template_id.toString());
    }

    return this.http.get<{ data: PdfTemplateSettings }>(
      `${this.accountingUrl}/api/v1/pdf-settings/default/${entityType}`,
      { params: httpParams }
    );
  }

  /**
   * Create a new PDF template setting
   */
  createPdfTemplateSetting(request: CreatePdfSettingsRequest): Observable<{ data: PdfTemplateSettings }> {
    return this.http.post<{ data: PdfTemplateSettings }>(
      `${this.accountingUrl}/api/v1/pdf-settings`,
      request
    );
  }

  /**
   * Update an existing PDF template setting
   */
  updatePdfTemplateSetting(id: number, request: UpdatePdfSettingsRequest): Observable<{ data: PdfTemplateSettings }> {
    return this.http.put<{ data: PdfTemplateSettings }>(
      `${this.accountingUrl}/api/v1/pdf-settings/${id}`,
      request
    );
  }

  /**
   * Delete a PDF template setting
   */
  deletePdfTemplateSetting(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.accountingUrl}/api/v1/pdf-settings/${id}`
    );
  }

  // ============================================
  // Form Templates (Entity-specific)
  // ============================================

  /**
   * Get form templates for a specific entity type
   * Maps entity types to their corresponding form template API endpoints
   */
  getFormTemplatesByEntityType(entityType: string): Observable<{ data: { id: string | number; name: string }[] }> {
    const endpointMap: Record<string, string> = {
      'client_invoice': 'client-invoice-form-templates',
      'client_order': 'client-order-form-templates',
      'client_credit_note': 'client-credit-note-form-templates',
      'client_payment': 'client-payment-form-templates',
      'vendor_invoice': 'vendor-invoice-form-templates',
      'vendor_po': 'vendor-po-form-templates',
      'vendor_debit_note': 'vendor-debit-note-form-templates',
      'vendor_payment': 'vendor-payment-form-templates',
      'grn': 'grn-form-templates',
      'journal_entry': 'journal-voucher-form-templates',
    };

    const endpoint = endpointMap[entityType];
    if (!endpoint) {
      // Return empty observable if entity type is not mapped
      return new Observable(subscriber => {
        subscriber.next({ data: [] });
        subscriber.complete();
      });
    }

    return this.http.get<{ data: { id: string | number; name: string }[] }>(
      `${this.accountingUrl}/api/v1/${endpoint}/`,
      { withCredentials: true }
    );
  }
}

